import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Card,
    CardContent,
    Avatar,
    Chip,
    Button,
    Divider,
    Tabs,
    Tab,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    Tooltip,
    TextField,
    Switch,
    FormControlLabel,
    InputAdornment,
    Select,
    MenuItem,
    Rating,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    RadioGroup,
    FormControlLabel as MuiFormControlLabel,
    Radio
} from '@mui/material';
import {
    Person,
    Email,
    Phone,
    Work,
    Business,
    CalendarToday,
    Star,
    CheckCircle,
    AccessTime,
    Timeline,
    ThumbUp,
    ExpandMore,
    Send,
    Print,
    History,
    TrendingUp,
    Assignment,
    Comment,
    Task,
    AssignmentTurnedIn,
    School,
    WorkspacePremium,
    PriorityHigh,
    CheckCircleOutline,
    Info,
    Edit,
    ThumbUpAlt,
    ThumbDownAlt
} from "@mui/icons-material";
import Chart from 'react-apexcharts';

// Dummy data for a new category
const initialNewCategory = { title: '', rating: 0, color: '#000000' };

const HREmployeeReviewDetails = ({ employee, onFinalize }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [isEditing, setIsEditing] = useState(false);
    const [isVisibleToEmployee, setIsVisibleToEmployee] = useState(false);

    // Initial Static Data - UPDATED with promotion eligibility
    const promotionAssessmentData = {
        currentRole: "Software Engineer",
        recommendedRole: "Senior Software Engineer",
        salaryIncrement: 15,
        effectiveDate: "2023-07-01",
        justification: "Has consistently exceeded performance expectations and demonstrated technical leadership. Has taken on additional responsibilities including mentoring junior developers and leading small projects. Ready for the next level.",
        presentSalary: 80000,
        isEligibleForPromotion: "yes", // NEW FIELD: yes/no
        managerPromotionRecommendation: "Strongly recommended for promotion based on exceptional performance and leadership qualities." // NEW FIELD
    };
    
    const performanceCategoriesData = [
        { id: 1, title: "Technical Skills", rating: 4.5, color: "#4CAF50" },
        { id: 2, title: "Problem-Solving", rating: 4.0, color: "#2196F3" },
        { id: 3, title: "Communication", rating: 3.5, color: "#FF9800" },
        { id: 4, title: "Teamwork", rating: 4.5, color: "#9C27B0" },
        { id: 5, title: "Adherence to Deadlines", rating: 4.0, color: "#F44336" }
    ];

    const managerFeedbackData = {
        strengths: "Excellent technical skills and problem-solving abilities. Consistently delivers high-quality work and meets deadlines. Takes initiative in identifying and resolving issues before they escalate.",
        improvements: "Could improve communication with stakeholders and documentation practices. Sometimes focuses too much on technical details without considering the bigger picture.",
        goals: "Continue developing leadership skills by mentoring junior team members. Improve project estimation accuracy. Take on more client-facing responsibilities.",
        comments: "Overall, a valuable team member who consistently performs above expectations. Has shown significant growth over the past year and is ready for more responsibilities."
    };

    // NEW STATE: Band assignment data
    const [bandData, setBandData] = useState({
        currentBand: 'B',
        previousBand: 'C',
        assignedBand: 'A',
        bandJustification: "Exceptional performance across all categories with demonstrated leadership and innovation. Consistently exceeds expectations in technical deliverables and has shown remarkable growth in leadership capabilities.",
        appraisalPeriod: "2024",
        bands: [
            { id: 'A', name: 'Band A - Exceptional', description: 'Top 10% performers', color: '#4CAF50', targetPercentage: 10 },
            { id: 'B', name: 'Band B - High', description: 'Next 20% performers', color: '#2196F3', targetPercentage: 20 },
            { id: 'C', name: 'Band C - Solid', description: 'Middle 40% performers', color: '#FF9800', targetPercentage: 40 },
            { id: 'D', name: 'Band D - Developing', description: 'Next 20% performers', color: '#F44336', targetPercentage: 20 },
            { id: 'E', name: 'Band E - Unsatisfactory', description: 'Bottom 10% performers', color: '#757575', targetPercentage: 10 }
        ]
    });

    // STATE MANAGEMENT
    const [promotionData, setPromotionData] = useState(promotionAssessmentData);
    const [categories, setCategories] = useState(performanceCategoriesData);
    const [newCategory, setNewCategory] = useState(initialNewCategory);
    const [editableManagerFeedback, setEditableManagerFeedback] = useState(managerFeedbackData);

    // Band distribution data for the chart
    const bandDistributionData = {
        options: {
            chart: {
                type: 'bar',
                toolbar: { show: false }
            },
            plotOptions: {
                bar: {
                    distributed: true,
                    borderRadius: 4,
                    columnWidth: '60%',
                }
            },
            xaxis: {
                categories: bandData.bands.map(band => band.id),
                title: { text: 'Performance Bands' }
            },
            yaxis: {
                title: { text: 'Percentage of Employees' },
                max: 100
            },
            colors: bandData.bands.map(band => band.color),
            dataLabels: {
                enabled: true,
                formatter: function (val) {
                    return val + "%";
                }
            },
            tooltip: {
                y: {
                    formatter: function (val) {
                        return val + "% of employees";
                    }
                }
            }
        },
        series: [{
            name: 'Employee Distribution',
            data: bandData.bands.map(band => band.targetPercentage)
        }]
    };

    // Band trend data (showing employee's band progression)
    const bandTrendData = {
        options: {
            chart: {
                type: 'line',
                toolbar: { show: false }
            },
            stroke: { curve: 'smooth', width: 3 },
            markers: { size: 6 },
            xaxis: {
                categories: ['2021', '2022', '2023', '2024'],
                title: { text: 'Appraisal Year' }
            },
            yaxis: {
                categories: ['E', 'D', 'C', 'B', 'A'],
                title: { text: 'Performance Band' },
                min: 0,
                max: 4,
                tickAmount: 5
            },
            colors: ['#9C27B0']
        },
        series: [{
            name: 'Band Progression',
            data: [1, 2, 2, 3] // Index positions corresponding to bands E(0), D(1), C(2), B(3), A(4)
        }]
    };

    useEffect(() => {
        setPromotionData(promotionAssessmentData);
        setCategories(performanceCategoriesData);
        setEditableManagerFeedback(managerFeedbackData);
        setIsVisibleToEmployee(employee?.hrStatus === 'finalized' ? true : false); 
    }, [employee]);

    if (!employee) {
        return (
            <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 3 }}>
                <Typography variant="body1" align="center">
                    Please select an employee to view details
                </Typography>
            </Paper>
        );
    }

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleToggleEdit = () => {
        setIsEditing(!isEditing);
        if (isEditing) {
            console.log('Saving changes:', { promotionData, categories, editableManagerFeedback, bandData });
            setNewCategory(initialNewCategory);
        }
    };
    
    const handleManagerFeedbackChange = (field, value) => {
        setEditableManagerFeedback(prev => ({ ...prev, [field]: value }));
    };

    const handlePromotionChange = (field, value) => {
        setPromotionData(prev => ({ ...prev, [field]: value }));
    };

    const handleRatingChange = (id, newRating) => {
        setCategories(prev => prev.map(cat => 
            cat.id === id ? { ...cat, rating: newRating } : cat
        ));
    };

    const handleNewCategoryChange = (field, value) => {
        setNewCategory(prev => ({ ...prev, [field]: value }));
    };

    const handleAddNewCategory = () => {
        if (newCategory.title && newCategory.rating > 0) {
            setCategories(prev => [
                ...prev, 
                { 
                    ...newCategory, 
                    id: Date.now(),
                    color: '#FF5722'
                }
            ]);
            setNewCategory(initialNewCategory);
        }
    };

    // Handler for band assignment
    const handleBandChange = (field, value) => {
        if (isEditing) {
            setBandData(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    // Handler for band justification
    const handleBandJustificationChange = (justification) => {
        if (isEditing) {
            setBandData(prev => ({
                ...prev,
                bandJustification: justification
            }));
        }
    };

    // Get band details by ID
    const getBandDetails = (bandId) => {
        return bandData.bands.find(band => band.id === bandId) || bandData.bands[0];
    };

    const getHRStatusChip = (status) => {
        switch (status) {
            case 'awaiting_manager':
                return <Chip label="Awaiting Manager" color="default" size="small" icon={<AccessTime fontSize="small" />} />;
            case 'pending_hr_review':
                return <Chip label="Pending HR Review" color="warning" size="small" icon={<PriorityHigh fontSize="small" />} />;
            case 'hr_reviewed':
                return <Chip label="HR Reviewed" color="info" size="small" icon={<CheckCircleOutline fontSize="small" />} />;
            case 'finalized':
                return <Chip label="Finalized" color="success" size="small" icon={<CheckCircle fontSize="small" />} />;
            default:
                return <Chip label="Unknown" color="default" size="small" />;
        }
    };

    // Get promotion eligibility chip
    const getPromotionEligibilityChip = (isEligible) => {
        switch (isEligible) {
            case 'yes':
                return <Chip label="Eligible" color="success" size="small" icon={<ThumbUpAlt fontSize="small" />} />;
            case 'no':
                return <Chip label="Not Eligible" color="error" size="small" icon={<ThumbDownAlt fontSize="small" />} />;
            default:
                return <Chip label="Pending" color="warning" size="small" icon={<AccessTime fontSize="small" />} />;
        }
    };

    const renderOverview = () => (
        <Grid container spacing={3}>
            {/* ... (Overview section remains exactly the same as previous version) */}
            <Grid item xs={12} md={4}>
                <Card sx={{ boxShadow: 2, borderRadius: 2, height: '100%' }}>
                    <CardContent>
                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Personal Information</Typography>
                        <List dense>
                            <ListItem><ListItemIcon><Person color="primary" /></ListItemIcon><ListItemText primary="Full Name" secondary={employee.name} /></ListItem>
                            <ListItem><ListItemIcon><Email color="primary" /></ListItemIcon><ListItemText primary="Email" secondary={`${employee.name.toLowerCase().replace(' ', '.')}@company.com`} /></ListItem>
                            <ListItem><ListItemIcon><Phone color="primary" /></ListItemIcon><ListItemText primary="Phone" secondary="+1 (555) 123-4567" /></ListItem>
                            <ListItem><ListItemIcon><Work color="primary" /></ListItemIcon><ListItemText primary="Position" secondary={employee.position} /></ListItem>
                            <ListItem><ListItemIcon><Business color="primary" /></ListItemIcon><ListItemText primary="Department" secondary={employee.department} /></ListItem>
                            <ListItem><ListItemIcon><CalendarToday color="primary" /></ListItemIcon><ListItemText primary="Join Date" secondary="Jan 15, 2021" /></ListItem>
                            
                            {/* Band Information */}
                            <ListItem>
                                <ListItemIcon>
                                    <WorkspacePremium color="primary" />
                                </ListItemIcon>
                                <ListItemText 
                                    primary="Previous Band" 
                                    secondary={
                                        <Chip 
                                            label={bandData.previousBand} 
                                            size="small"
                                            sx={{ 
                                                backgroundColor: getBandDetails(bandData.previousBand).color,
                                                color: 'white',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                    } 
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <TrendingUp color="primary" />
                                </ListItemIcon>
                                <ListItemText 
                                    primary="Current Appraisal Band" 
                                    secondary={
                                        isEditing ? (
                                            <Select
                                                value={bandData.assignedBand}
                                                onChange={(e) => handleBandChange('assignedBand', e.target.value)}
                                                size="small"
                                                sx={{ 
                                                    minWidth: 80,
                                                    '& .MuiSelect-select': { 
                                                        py: 0.5,
                                                        backgroundColor: getBandDetails(bandData.assignedBand).color,
                                                        color: 'white',
                                                        borderRadius: 1,
                                                        fontWeight: 'bold'
                                                    }
                                                }}
                                            >
                                                {bandData.bands.map((band) => (
                                                    <MenuItem key={band.id} value={band.id} sx={{ backgroundColor: band.color, color: 'white' }}>
                                                        {band.id}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        ) : (
                                            <Chip 
                                                label={bandData.assignedBand} 
                                                size="small"
                                                sx={{ 
                                                    backgroundColor: getBandDetails(bandData.assignedBand).color,
                                                    color: 'white',
                                                    fontWeight: 'bold'
                                                }}
                                            />
                                        )
                                    } 
                                />
                            </ListItem>
                            {isEditing && (
                                <ListItem>
                                    <ListItemIcon>
                                        <CalendarToday color="primary" />
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary="Appraisal Period" 
                                        secondary={
                                            <TextField
                                                size="small"
                                                value={bandData.appraisalPeriod}
                                                onChange={(e) => handleBandChange('appraisalPeriod', e.target.value)}
                                                sx={{ width: 120 }}
                                            />
                                        } 
                                    />
                                </ListItem>
                            )}
                        </List>
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12} md={8}>
                <Card sx={{ boxShadow: 2, borderRadius: 2, height: '100%' }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" fontWeight="bold">Performance Overview</Typography>
                            <Box>{getHRStatusChip(employee.hrStatus)}</Box>
                        </Box>
                        
                        {/* Band Performance Summary */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} sm={4}>
                                <Paper 
                                    sx={{ 
                                        p: 2, 
                                        borderRadius: 2, 
                                        backgroundColor: `${getBandDetails(bandData.assignedBand).color}20`,
                                        border: `2px solid ${getBandDetails(bandData.assignedBand).color}`,
                                        textAlign: 'center'
                                    }}
                                >
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Current Band
                                    </Typography>
                                    <Typography 
                                        variant="h4" 
                                        fontWeight="bold" 
                                        sx={{ 
                                            color: getBandDetails(bandData.assignedBand).color,
                                            my: 1
                                        }}
                                    >
                                        {bandData.assignedBand}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {getBandDetails(bandData.assignedBand).name.split(' - ')[1]}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Appraisal {bandData.appraisalPeriod}
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Paper 
                                    sx={{ 
                                        p: 2, 
                                        borderRadius: 2, 
                                        backgroundColor: '#f5f5f5',
                                        border: '2px solid #e0e0e0',
                                        textAlign: 'center'
                                    }}
                                >
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Previous Band
                                    </Typography>
                                    <Typography 
                                        variant="h4" 
                                        fontWeight="bold" 
                                        sx={{ 
                                            color: getBandDetails(bandData.previousBand).color,
                                            my: 1
                                        }}
                                    >
                                        {bandData.previousBand}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {getBandDetails(bandData.previousBand).name.split(' - ')[1]}
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Paper 
                                    sx={{ 
                                        p: 2, 
                                        borderRadius: 2, 
                                        backgroundColor: '#e3f2fd',
                                        border: '2px solid #2196f3',
                                        textAlign: 'center'
                                    }}
                                >
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Band Change
                                    </Typography>
                                    <Typography 
                                        variant="h6" 
                                        fontWeight="bold" 
                                        sx={{ 
                                            color: bandData.assignedBand > bandData.previousBand ? '#4CAF50' : 
                                                   bandData.assignedBand < bandData.previousBand ? '#F44336' : '#FF9800',
                                            my: 1
                                        }}
                                    >
                                        {bandData.assignedBand > bandData.previousBand ? '↑ Improved' : 
                                         bandData.assignedBand < bandData.previousBand ? '↓ Declined' : '→ Maintained'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {bandData.previousBand} → {bandData.assignedBand}
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        {/* Band Distribution Chart */}
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                            Company Band Distribution
                        </Typography>
                        <Chart 
                            options={bandDistributionData.options} 
                            series={bandDistributionData.series} 
                            type="bar" 
                            height={200} 
                        />

                        <Divider sx={{ my: 2 }} />

                        {/* Band Progression Trend */}
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                            Band Progression Trend
                        </Typography>
                        <Chart 
                            options={bandTrendData.options} 
                            series={bandTrendData.series} 
                            type="line" 
                            height={200} 
                        />
                    </CardContent>
                </Card>
            </Grid>

            {/* Band Justification Section */}
            <Grid item xs={12}>
                <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" fontWeight="bold">
                                Band Assignment Justification
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Appraisal Period: {bandData.appraisalPeriod}
                            </Typography>
                        </Box>
                        {isEditing ? (
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                value={bandData.bandJustification}
                                onChange={(e) => handleBandJustificationChange(e.target.value)}
                                placeholder="Provide rationale for the assigned band..."
                                variant="outlined"
                            />
                        ) : (
                            <Paper sx={{ p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                                <Typography variant="body1">
                                    {bandData.bandJustification}
                                </Typography>
                            </Paper>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            {/* Compact Band Definitions */}
            <Grid item xs={12}>
                <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
                    <CardContent>
                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                            Performance Band Definitions
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Band</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Performance Level</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>Description</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Target %</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Color</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {bandData.bands.map((band) => (
                                        <TableRow 
                                            key={band.id}
                                            sx={{ 
                                                '&:last-child td, &:last-child th': { border: 0 },
                                                backgroundColor: band.id === bandData.assignedBand ? `${band.color}15` : 'inherit'
                                            }}
                                        >
                                            <TableCell>
                                                <Chip 
                                                    label={band.id} 
                                                    size="small"
                                                    sx={{ 
                                                        backgroundColor: band.color,
                                                        color: 'white',
                                                        fontWeight: 'bold',
                                                        width: 40
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'medium' }}>
                                                {band.name.split(' - ')[1]}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {band.description}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={`${band.targetPercentage}%`} 
                                                    size="small" 
                                                    variant="outlined"
                                                    color="primary"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box 
                                                    sx={{ 
                                                        width: 20, 
                                                        height: 20, 
                                                        backgroundColor: band.color,
                                                        borderRadius: 1,
                                                        border: '1px solid #ddd'
                                                    }} 
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box sx={{ mt: 2, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                💡 <strong>Current Assignment Highlighted</strong> - The band assigned for the current appraisal period is highlighted for quick reference.
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    {employee.hrStatus === 'pending_hr_review' && (
                        <Button variant="contained" color="success" startIcon={<Send />} onClick={onFinalize}>
                            Finalize Review
                        </Button>
                    )}
                    {employee.hrStatus === 'hr_reviewed' && (
                        <Button variant="contained" color="success" startIcon={<Send />} onClick={onFinalize}>
                            Send to Employee
                        </Button>
                    )}
                    {employee.hrStatus === 'finalized' && (
                        <Button variant="outlined" color="primary" startIcon={<Print />}>
                            Print Review
                        </Button>
                    )}
                </Box>
            </Grid>
        </Grid>
    );

    const renderManagerFeedback = () => (
        <Grid container spacing={3}>
            {/* ... (Manager Feedback section remains exactly the same) */}
            <Grid item xs={12}>
                <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
                    <CardContent>
                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                            <Star sx={{ mr: 1, color: '#FFC107' }} />
                            Category Ratings
                            <Tooltip title={isEditing ? "Save changes" : "Edit Category Ratings"}>
                                <IconButton 
                                    size="small" 
                                    color={isEditing ? "success" : "default"} 
                                    onClick={handleToggleEdit}
                                    sx={{ ml: 1 }}
                                >
                                    {isEditing ? <CheckCircle /> : <Edit />}
                                </IconButton>
                            </Tooltip>
                        </Typography>
                        <Grid container spacing={2}>
                            {categories.map((category) => (
                                <Grid item xs={12} sm={6} md={4} key={category.id}>
                                    <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${category.color}30` }}>
                                        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>{category.title}</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Rating
                                                value={category.rating}
                                                precision={0.5}
                                                readOnly={!isEditing}
                                                onChange={(event, newValue) => { if (isEditing) handleRatingChange(category.id, newValue); }}
                                                size="medium"
                                                sx={{ '& .MuiRating-iconFilled': { color: category.color } }}
                                            />
                                            <Typography variant="h6" fontWeight="bold" sx={{ color: category.color }}>{category.rating.toFixed(1)}</Typography>
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                        
                        {isEditing && (
                            <Box sx={{ mt: 3, p: 2, border: '1px dashed #ddd', borderRadius: 2 }}>
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Add New Category</Typography>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} sm={4}>
                                        <TextField fullWidth size="small" label="Category Title" value={newCategory.title} onChange={(e) => handleNewCategoryChange('title', e.target.value)} />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2">Rating:</Typography>
                                            <Rating
                                                value={newCategory.rating}
                                                precision={0.5}
                                                onChange={(event, newValue) => { handleNewCategoryChange('rating', newValue); }}
                                                size="medium"
                                            />
                                            <Typography variant="body2" fontWeight="bold">({newCategory.rating.toFixed(1)})</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Button 
                                            variant="contained" 
                                            color="primary" 
                                            onClick={handleAddNewCategory}
                                            disabled={!newCategory.title || newCategory.rating === 0}
                                            startIcon={<Task />}
                                        >
                                            Add Category
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12}>
                <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                                <Comment sx={{ mr: 1, color: '#2196F3' }} />
                                Manager's Qualitative Feedback
                            </Typography>
                             <Tooltip title={isEditing ? "Save changes" : "Edit Manager Feedback"}>
                                <IconButton 
                                    color={isEditing ? "success" : "default"} 
                                    onClick={handleToggleEdit}
                                >
                                    {isEditing ? <CheckCircle /> : <Edit />}
                                </IconButton>
                            </Tooltip>
                        </Box>
                        
                        <Grid container spacing={3}>
                            {['strengths', 'improvements', 'goals'].map((field) => (
                                <Grid item xs={12} md={4} key={field}>
                                    <Card sx={{
                                        height: '100%',
                                        background: field === 'strengths' ? 'linear-gradient(135deg, white 60%, #4CAF5033 100%)' : field === 'improvements' ? 'linear-gradient(135deg, white 60%, #FF980033 100%)' : 'linear-gradient(135deg, white 60%, #2196F333 100%)',
                                        border: field === 'strengths' ? '1px solid #4CAF5033' : field === 'improvements' ? '1px solid #FF980033' : '1px solid #2196F333'
                                    }}>
                                        <CardContent>
                                            <Typography variant="h6" fontWeight="bold" color={field === 'strengths' ? 'success.main' : field === 'improvements' ? 'warning.main' : 'primary.main'} sx={{ mb: 2 }}>
                                                {field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')}
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={isEditing ? 5 : 3}
                                                value={editableManagerFeedback[field]}
                                                onChange={(e) => handleManagerFeedbackChange(field, e.target.value)}
                                                disabled={!isEditing}
                                                variant={isEditing ? "outlined" : "standard"}
                                                InputProps={{ disableUnderline: !isEditing }}
                                                sx={{
                                                    '& .MuiInputBase-root': {
                                                        bgcolor: isEditing ? 'white' : 'transparent',
                                                        border: isEditing ? '1px solid #ccc' : 'none',
                                                        borderRadius: 1,
                                                        p: isEditing ? 1 : 0
                                                    }
                                                }}
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                        
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                Additional Comments
                            </Typography>
                            <Paper sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={isEditing ? 4 : 2}
                                    value={editableManagerFeedback.comments}
                                    onChange={(e) => handleManagerFeedbackChange('comments', e.target.value)}
                                    disabled={!isEditing}
                                    variant={isEditing ? "outlined" : "standard"}
                                    InputProps={{ disableUnderline: !isEditing }}
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            bgcolor: isEditing ? 'white' : 'transparent',
                                            border: isEditing ? '1px solid #ccc' : 'none',
                                            borderRadius: 1,
                                            p: isEditing ? 1 : 0
                                        }
                                    }}
                                />
                            </Paper>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>

            {/* UPDATED: HR Promotion Assessment with new fields */}
            <Grid item xs={12}>
                <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                                <TrendingUp sx={{ mr: 1, color: '#9C27B0' }} />
                                HR Promotion Assessment
                            </Typography>
                            <Tooltip title={isEditing ? "Save changes" : "Edit Promotion Assessment"}>
                                <IconButton 
                                    color={isEditing ? "success" : "default"} 
                                    onClick={handleToggleEdit}
                                >
                                    {isEditing ? <CheckCircle /> : <Edit />}
                                </IconButton>
                            </Tooltip>
                        </Box>
                        
                        {/* UPDATED: Static Fields with new promotion eligibility */}
                        <Grid container spacing={3} sx={{ mb: 3 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#e8f5e9' }}>
                                    <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
                                        Present Role
                                    </Typography>
                                    <Typography variant="h6" fontWeight="medium" color="success.dark">
                                        {promotionAssessmentData.currentRole}
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#e8f5e9' }}>
                                    <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
                                        Present Annual Salary
                                    </Typography>
                                    <Typography variant="h6" fontWeight="medium" color="success.dark">
                                        ${promotionAssessmentData.presentSalary.toLocaleString()}
                                    </Typography>
                                </Paper>
                            </Grid>
                            {/* NEW: Promotion Eligibility Field */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper sx={{ 
                                    p: 2, 
                                    borderRadius: 2, 
                                    bgcolor: promotionData.isEligibleForPromotion === 'yes' ? '#e8f5e9' : 
                                             promotionData.isEligibleForPromotion === 'no' ? '#ffebee' : '#fff3e0',
                                    border: promotionData.isEligibleForPromotion === 'yes' ? '2px solid #4CAF50' : 
                                            promotionData.isEligibleForPromotion === 'no' ? '2px solid #F44336' : '2px solid #FF9800'
                                }}>
                                    <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
                                        Eligible for Promotion
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                                        {getPromotionEligibilityChip(promotionData.isEligibleForPromotion)}
                                        {isEditing ? (
                                            <RadioGroup
                                                row
                                                value={promotionData.isEligibleForPromotion}
                                                onChange={(e) => handlePromotionChange('isEligibleForPromotion', e.target.value)}
                                                sx={{ ml: 1 }}
                                            >
                                                <MuiFormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
                                                <MuiFormControlLabel value="no" control={<Radio size="small" />} label="No" />
                                            </RadioGroup>
                                        ) : null}
                                    </Box>
                                </Paper>
                            </Grid>
                            {/* NEW: Manager Recommendation Summary */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#e3f2fd', border: '2px solid #2196F3' }}>
                                    <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
                                        Manager Recommendation
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                                        {promotionData.isEligibleForPromotion === 'yes' ? 
                                         "Recommended for promotion" : 
                                         "Not recommended at this time"}
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* NEW: Manager Promotion Recommendation Details */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: 'primary.main' }}>
                                Manager's Promotion Recommendation
                            </Typography>
                            <Paper sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                                {isEditing ? (
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        value={promotionData.managerPromotionRecommendation}
                                        onChange={(e) => handlePromotionChange('managerPromotionRecommendation', e.target.value)}
                                        placeholder="Manager's comments on promotion eligibility..."
                                        variant="outlined"
                                    />
                                ) : (
                                    <Typography variant="body1">
                                        {promotionData.managerPromotionRecommendation}
                                    </Typography>
                                )}
                            </Paper>
                        </Box>
                        
                        <Divider sx={{ my: 2 }} />
                        
                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: 'primary.main' }}>
                            HR Recommendation (Editable)
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    fullWidth
                                    label="Recommended Role"
                                    value={promotionData.recommendedRole || ''}
                                    onChange={(e) => handlePromotionChange('recommendedRole', e.target.value)}
                                    disabled={!isEditing}
                                    size="medium"
                                />
                            </Grid>
                            
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    fullWidth
                                    label="Salary Increment"
                                    type="number"
                                    value={promotionData.salaryIncrement || ''}
                                    onChange={(e) => handlePromotionChange('salaryIncrement', e.target.value)}
                                    disabled={!isEditing}
                                    size="medium"
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                    }}
                                />
                            </Grid>
                            
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    fullWidth
                                    label="Effective Date"
                                    type="date"
                                    value={promotionData.effectiveDate || ''}
                                    onChange={(e) => handlePromotionChange('effectiveDate', e.target.value)}
                                    disabled={!isEditing}
                                    size="medium"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                        
                        <Box sx={{ mt: 3 }}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="HR Justification / Rationale"
                                value={promotionData.justification || ''}
                                onChange={(e) => handlePromotionChange('justification', e.target.value)}
                                disabled={!isEditing}
                            />
                        </Box>
                        
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );

    const renderGoals = () => (
        <Typography>Goals Section</Typography>
    );

    const renderTraining = () => (
        <Typography>Training Section</Typography>
    );

    return (
        <Box>
            <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 3, mb: 3 }}>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pb: 1, borderBottom: '1px solid #eee' }}>
                    <Typography variant="h6" fontWeight="bold" color="text.secondary">
                        Appraisal Period: <Chip label={`Yearly Review ${bandData.appraisalPeriod}`} color="primary" sx={{ ml: 1, fontWeight: 'bold' }} />
                    </Typography>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={isVisibleToEmployee}
                                onChange={(e) => setIsVisibleToEmployee(e.target.checked)}
                                color="primary"
                                disabled={isEditing}
                            />
                        }
                        label={
                            <Typography fontWeight="bold">
                                Visible to Employee
                            </Typography>
                        }
                        sx={{ mr: 0 }}
                    />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar
                        alt={employee.name}
                        src={employee.avatar}
                        sx={{ width: 80, height: 80, mr: 2 }}
                    >
                        {employee.name.charAt(0)}
                    </Avatar>
                    <Box>
                        <Typography variant="h5" fontWeight="bold">
                            {employee.name}
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                            {employee.position} • {employee.department}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            {getHRStatusChip(employee.hrStatus)}
                            <Typography variant="body2" sx={{ ml: 2 }}>
                                Manager: {employee.managerName}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Tooltip title={isEditing ? "Save Changes" : "Enter HR Edit Mode"}>
                            <Button
                                variant={isEditing ? "contained" : "outlined"}
                                color={isEditing ? "success" : "primary"}
                                startIcon={isEditing ? <CheckCircle /> : <Edit />}
                                onClick={handleToggleEdit}
                            >
                                {isEditing ? "Save & Exit Edit" : "Edit HR Assessment"}
                            </Button>
                        </Tooltip>
                        
                        {employee.hrStatus !== 'finalized' && (
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<Send />}
                                onClick={onFinalize}
                                disabled={isEditing}
                            >
                                {employee.hrStatus === 'pending_hr_review' ? "Finalize Review" : "Send to Employee"}
                            </Button>
                        )}
                    </Box>
                </Box>

                <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tab label="Overview" icon={<Info />} iconPosition="start" />
                    <Tab label="Manager Feedback & Assessment" icon={<Comment />} iconPosition="start" />
                    <Tab label="Past Goals" icon={<AssignmentTurnedIn />} iconPosition="start" />
                    <Tab label="Past Appraisals" icon={<School />} iconPosition="start" />
                </Tabs>
            </Paper>

            <Box sx={{ mt: 3 }}>
                {activeTab === 0 && renderOverview()}
                {activeTab === 1 && renderManagerFeedback()}
                {activeTab === 2 && renderGoals()}
                {activeTab === 3 && renderTraining()}
            </Box>
        </Box>
    );
};

export default HREmployeeReviewDetails;